import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, CalendarClock, Check, Loader2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { OfficeMap } from "@/components/OfficeMap";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { ENQUIRY_FORM_ID } from "@/hooks/useBookConsultation";
import { JURISDICTIONS } from "@/data/jurisdictions";

export default function ContactPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [searchParams] = useSearchParams();

  // Prefill the note when arriving from a jurisdiction page ("Discuss {name}").
  useEffect(() => {
    const slug = searchParams.get("jurisdiction");
    if (!slug) return;
    const j = JURISDICTIONS.find((x) => x.slug === slug);
    if (j) setMessage(`I'd like to discuss company incorporation in ${j.name}.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentMeta({
    title: "Contact",
    description:
      "Contact AMADI Legal Consultancy to discuss corporate structuring, compliance, and cross-border matters across the UAE and Africa.",
    path: "/contact",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) return;
    setSending(true);
    try {
      await api.post("/api/enquiries", { email, message, website, source: "contact-form" });
      setSubmitted(true);
      setEmail("");
      setMessage("");
      setWebsite("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Let's discuss your matter."
        subtitle="Reach the firm directly, or book a consultation at a time that suits you."
        current="Contact"
      />

      <section className="bg-canvas py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left — enquiry + address */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                Enquiries
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight max-w-md">
              Send us a note.
            </h2>

            <form data-testid="contact-form" id={ENQUIRY_FORM_ID} onSubmit={handleSubmit} className="mt-10 max-w-md scroll-mt-28">
              <label
                htmlFor="contact-email"
                className="block font-sans text-[11px] tracking-[0.2em] uppercase text-charcoal/50 mb-3"
              >
                Email Address
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full bg-transparent border border-charcoal/20 px-4 py-4 font-serif text-base text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-forest transition-colors duration-300"
              />

              <label
                htmlFor="contact-message"
                className="block font-sans text-[11px] tracking-[0.2em] uppercase text-charcoal/50 mb-3 mt-6"
              >
                How can we help?
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="A brief note about your matter…"
                className="w-full bg-transparent border border-charcoal/20 px-4 py-4 font-serif text-base text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-forest transition-colors duration-300"
              />

              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="hidden"
                aria-hidden="true"
                data-testid="contact-honeypot"
              />

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-forest text-canvas border border-forest transition-colors duration-300 hover:bg-charcoal hover:border-charcoal disabled:opacity-60"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
              {error && (
                <p data-testid="contact-error-message" className="mt-4 font-sans text-[13px] text-red-600">
                  {error}
                </p>
              )}
              {submitted && (
                <p
                  data-testid="contact-success-message"
                  className="mt-4 flex items-center gap-2 font-sans text-[13px] text-forest"
                >
                  <Check className="w-4 h-4" strokeWidth={2} />
                  Thank you — we have received your details and will be in touch shortly.
                </p>
              )}
            </form>

            {/* Direct details */}
            <div className="mt-14 space-y-8">
              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-10 h-10 border border-charcoal/15 text-forest shrink-0">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-charcoal/45 mb-1">
                    Email
                  </p>
                  <a
                    data-testid="contact-email-link"
                    href="mailto:connect@amadi.io"
                    className="font-serif text-lg text-charcoal hover:text-forest transition-colors duration-300"
                  >
                    connect@amadi.io
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Calendly + Map (embeds drop in here later) */}
          <div className="space-y-10">
            <div>
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-forest mb-4">
                Book a Consultation
              </p>
              <div
                data-testid="calendly-placeholder"
                className="w-full aspect-[4/3] bg-[#F1F1EE] border border-charcoal/15 flex flex-col items-center justify-center text-center px-6"
              >
                <CalendarClock className="w-10 h-10 text-charcoal/25" strokeWidth={1.2} />
                <span className="mt-4 font-serif text-lg text-charcoal/70">
                  Online scheduling will be available shortly.
                </span>
                <span className="mt-1.5 font-sans text-[11px] tracking-[0.14em] text-charcoal/40">
                  In the meantime, reach us by email above.
                </span>
              </div>
            </div>

            <div>
              <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-forest mb-4">
                Find Us
              </p>
              <p className="font-serif text-base text-charcoal/70 mb-4 max-w-md">
                Advising clients across five markets — the Middle East, Africa, and India.
              </p>
              <OfficeMap />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
